import { createContext, memo, useContext, useMemo } from 'react';
import { Mutate, StateCreator, StoreApi, StoreMutatorIdentifier, UseBoundStore } from 'zustand';
import { createStore as createZustandStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';

interface CreateStoreContextApi<T, Mos extends [StoreMutatorIdentifier, unknown][] = []> {
  store: Mutate<StoreApi<T>, Mos>;
  /**
   * Provider to wrap the store context around the application.
   * this allows the store to be accessible to all the children components isolated from the global store.
   * if no store is passed it will create a new store instance.
   */
  StoreProvider: React.FC<
    React.PropsWithChildren<{
      store?: Mutate<StoreApi<T>, Mos>;
    }>
  >;
  /**
   * Store context to be used to access the store instance.
   */
  StoreContext: React.Context<Mutate<StoreApi<T>, Mos>>;
  /**
   * Creates a new store instance.
   */
  createStore: () => Mutate<StoreApi<T>, Mos>;
  /**
   * Hook to create a new store instance, some folks might prefer
   * this over the `createStore` method just for naming consistency.
   */
  useCreateStore: () => Mutate<StoreApi<T>, Mos>;
  /**
   * Use the bounded store instance by consuming the store context or global store.
   */
  useStore: UseBoundStore<Mutate<StoreApi<T>, Mos>>;

  /**
   * Use the store api directly.
   */
  useStoreApi: () => Mutate<StoreApi<T>, Mos>;
}

type CreateStoreContext = {
  <T, Mos extends [StoreMutatorIdentifier, unknown][] = []>(
    initializer: StateCreator<T, [], Mos>
  ): CreateStoreContextApi<T, Mos>;
  <T>(): <Mos extends [StoreMutatorIdentifier, unknown][] = []>(
    initializer: StateCreator<T, [], Mos>
  ) => CreateStoreContextApi<T, Mos>;
};

/**
 * React tool to create a store context using the zustand.
 *
 * due `createContext` deprecation from zustand community
 *
 * @see https://docs.pmnd.rs/zustand/previous-versions/zustand-v3-create-context#migration
 *
 * @example
 * ```ts
 * type State = {
 *  count: number;
 *  increment: (amount: number) => void;
 * }
 * const { StoreProvider, StoreContext, useCreateStore, useStore, store } = createStoreContext<State>((set) => ({
 *   count: 0,
 *   increment: (amount) => set((state) => ({ count: state.count + amount })),
 * }))
 * ```
 */
export const createStoreContext = ((initializer) => {
  const createStore = () => createZustandStore(initializer);
  const useCreateStore = () => createStore();
  const store = createStore();
  const StoreContext = createContext<StoreApi<any>>(store);
  const useStoreApi = () => useContext(StoreContext);
  const useStore = (selector: any, equalityFn: any) => {
    return useStoreWithEqualityFn(useStoreApi(), selector, equalityFn);
  };

  const StoreProvider: React.FC<
    React.PropsWithChildren<{
      store?: StoreApi<any>;
    }>
  > = memo(({ children, store }) => {
    return (
      <StoreContext.Provider value={useMemo(() => store ?? createStore(), [store])}>
        {children}
      </StoreContext.Provider>
    );
  });

  return {
    store,
    StoreProvider,
    StoreContext,
    createStore,
    useCreateStore,
    useStore,
    useStoreApi,
  } as any;
}) as CreateStoreContext;
